import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PlayerBar from './PlayerBar';
import { usePlayerStore } from '../store/playerStore';

interface LandingLayoutProps {
  children: React.ReactNode;
}

export default function LandingLayout({ children }: LandingLayoutProps) {
  const { i18n, t } = useTranslation();
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const currentTrack = usePlayerStore((state) => state.currentTrack);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsLangMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-dark-950 text-white">
      {/* Landing Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-dark-900/90 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 group">
              <img src="/landing/logo-light.svg" alt="Fonorex" className="h-10" />
            </Link>

            {/* Right Side */}
            <div className="flex items-center space-x-4">
              {/* Phone */}
              <a
                href={`tel:${t('landing.phone')}`}
                className="hidden md:block text-gray-300 hover:text-white transition-all"
              >
                {t('landing.phone')}
              </a>

              {/* Language Switcher */}
              <div className="relative">
                <button
                  onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                  className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-primary-600/80 to-secondary-600/80 hover:from-primary-500 hover:to-secondary-500 rounded-lg transition-all flex items-center space-x-2 shadow-lg"
                >
                  <span className="text-lg">🌐</span>
                  <span className="uppercase font-bold">{i18n.language}</span>
                  <span className={`text-xs transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`}>▼</span>
                </button>
                {isLangMenuOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-dark-900 border border-white/20 rounded-lg shadow-2xl z-50 animate-scale-in">
                    {[
                      { code: 'es', name: '🇪🇸 Español' },
                      { code: 'en', name: '🇬🇧 English' },
                      { code: 'ru', name: '🇷🇺 Русский' },
                      { code: 'lt', name: '🇱🇹 Lietuvių' },
                    ].map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`w-full px-4 py-3 text-left text-sm hover:bg-white/10 first:rounded-t-lg last:rounded-b-lg transition-all ${
                          i18n.language === lang.code
                            ? 'bg-gradient-to-r from-primary-600/30 to-secondary-600/30 text-white font-semibold'
                            : 'text-gray-300'
                        }`}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Login Button */}
              <Link
                to="/login"
                className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all"
              >
                {t('landing.login')}
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Content with padding for fixed header */}
      <div className="pt-20">
        {children}
      </div>

      {/* Footer */}
      <footer className={`bg-dark-950 border-t border-white/10 py-8 ${currentTrack ? 'mb-24' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center space-y-4">
            <div className="flex items-center space-x-3">
              <img src="/landing/logo-light.svg" alt="Fonorex" className="h-8" />
            </div>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
              <Link to="/about" className="text-gray-400 hover:text-white transition-colors">
                {t('footer.about')}
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">
                {t('footer.terms')}
              </Link>
              <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">
                {t('footer.privacy')}
              </Link>
            </div>
            <p className="text-center text-gray-400 text-sm">
              © 2024-2025 {t('landing.footer.copyright')}
            </p>
          </div>
        </div>
      </footer>

      {/* Player Bar */}
      <PlayerBar />
    </div>
  );
}
